import styled, {css} from "styled-components"
import {Grid} from "react-bootstrap"

import Link, {RouterLink, ButtonRouterLink} from "@irati/ui/src/Link"
import {TextButton} from "@irati/ui/src/Button"
import {shadows} from "@irati/ui/src/global/elevation"
import Flex from "@irati/ui/src/Flex"
import {PerkboxLogo} from "@irati/ui/src/Logos"

export const HEADER_DESKTOP_HEIGHT = "70px"
export const HEADER_MOBILE_HEIGHT = "50px"
export const HEADER_WITH_BANNER_HEIGHT = "115px"

interface HeaderGridProps {
  $isFullWidth?: boolean
}

interface HeaderWrapperProps {
  showAppBanner?: boolean
  isAdmin?: boolean
}

export const HeaderWrapper = styled(Flex).attrs({alignItems: "center"})<HeaderWrapperProps>`
  z-index: 30;
  width: 100%;
  position: relative;

  ${({theme, isAdmin, showAppBanner}) => css`
    height: ${showAppBanner ? HEADER_WITH_BANNER_HEIGHT : HEADER_MOBILE_HEIGHT};
    background-color: ${theme.colors.utility.white};
    ${!isAdmin &&
    css`
      position: fixed;
      top: 0;
      left: 0;
    `}
    ${theme.mediaQueries.minWidthMedia.tablet`
      border-bottom: 1px solid ${theme.colors.greys.mediumLight};
      height: ${HEADER_DESKTOP_HEIGHT};
    `}
  `}
`
export const HeaderContainer = styled(Flex).attrs({justifyContent: "space-between", width: "100%"})`
  ${({theme}) => css`
    background-color: ${theme.colors.utility.white};
    border-bottom: 1px solid ${theme.colors.greys.mediumLight};
  `}
`

export const TenantLogo = styled.img`
  ${({theme}) => css`
    margin-left: ${theme.spacing.one};
    height: ${theme.spacing.three};
  `}
`

export const HeaderSeparator = styled.div`
  ${({theme}) => css`
    border-left: 1px solid ${theme.colors.greys.grey03};
    margin: 27px 15px;
    height: 16px;
    ${theme.mediaQueries.minWidthMedia.tablet`
      margin: 17px 8px;
    `}
    ${theme.mediaQueries.minWidthMedia.desktop`
      margin: 27px 8px;
    `}
  `}
`

export const HeaderButtonStyle = styled(ButtonRouterLink)`
  margin-left: ${({theme}) => theme.spacing.one};
  min-width: 112px; /* Used to avoid button resizing as label text from API is loaded  */
  padding: 10px 28px;
`

export const HeaderGrid = styled(Grid)<HeaderGridProps>`
  background-color: ${({theme}) => theme.colors.greys.white};
  ${({$isFullWidth}) =>
    $isFullWidth &&
    css`
      width: 100%;
      margin-left: 0;
      margin-right: 0;
    `}
`

export const TenantName = styled.div`
  ${({theme}) => css`
    font-size: 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
    font-family: ${theme.font.light};
    margin-left: ${theme.spacing.two};
    color: ${theme.colors.utility.subdued};
    padding: ${`${theme.spacing.half} 0`};
    ${theme.mediaQueries.minWidthMedia.desktop`
      max-width: 300px;
    `}
    ${theme.mediaQueries.minWidthMedia.largeDesktop`
      max-width: 550px;
    `}
  `}
`

export const Hr = styled.hr`
  margin: 0;
`

interface LinksProps {
  height: number
  showAppBanner?: boolean
}

export const Links = styled.div<LinksProps>`
  position: absolute;
  top: -100vh;
  left: 0;
  right: 0;
  z-index: -1;
  background: ${({theme}) => theme.colors.greys.white};
  transition: top 0.2s;
  &.mobileLinksOpen {
    ${({showAppBanner}) => css`
      top: ${showAppBanner ? HEADER_WITH_BANNER_HEIGHT : HEADER_MOBILE_HEIGHT};
    `}
    ${shadows.low}
  }
  a {
    display: flex;
    letter-spacing: 0.6px;
    font-weight: 400;
    height: 60px;
    padding: 15px;
    font-size: 16px;
    text-transform: initial;
    font-family: ${({theme}) => theme.font.medium};
  }
  p {
    margin: 10px 15px;
  }
  hr {
    width: calc(100% - 30px);
    margin-left: 15px;
    margin-right: 15px;
  }
`

export const styling = css`
  display: flex;
  position: relative;
  flex-direction: row;
  height: 40px;
  align-items: center;
  border-radius: 4px;
  white-space: nowrap;
  ${({theme}) => css`
    color: ${theme.colors.greys.black};
    padding: 0 ${theme.spacing.one};
    &:hover,
    &:active,
    &:focus {
      background-color: ${theme.colors.greys.grey03};
      text-decoration: none;
    }
    ${theme.mediaQueries.maxWidthMedia.tablet`
      margin: ${theme.spacing.half} 0;
      &:hover, &:active, &:focus {
        background-color: ${theme.colors.greys.white};
        text-decoration: none;
      }
    `}
  `}
`

export default styled(RouterLink)`
  ${styling}
`
export const StyledHeaderButton = styled(TextButton)`
  ${styling}
`
export const StyledHeaderAnchor = styled(Link)`
  ${styling}
`

interface SecondaryNavProps {
  brandColor: string
}

export const SecondaryNavWrapper = styled.div<SecondaryNavProps>`
  background-color: ${({theme}) => theme.colors.greys.grey03};
  a {
    display: inline-block;
    padding: 15px 30px;
    transition: all 0.2;
    &:hover,
    &:focus {
      text-decoration: none;
    }
    &.active {
      background-color: ${({theme}) => theme.colors.greys.white};
      padding: 15px 30px 12px;
      border-bottom: 3px solid ${({brandColor}) => brandColor};
    }
  }
`

export const UnauthedHeader = styled(Flex)`
  ${({theme}) => css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    align-items: center;
    z-index: 30;
    background-color: ${theme.colors.utility.white};
    justify-content: space-between;
    padding: ${theme.spacing.one} ${theme.spacing.two};
    box-shadow: ${theme.shadows.low};
    height: ${HEADER_MOBILE_HEIGHT};
    ${theme.mediaQueries.minWidthMedia.tablet`
      height: ${HEADER_DESKTOP_HEIGHT};
    `}
  `}
`

export const LoginDesktopPerkboxLogo = styled(PerkboxLogo)`
  ${({theme}) => css`
    width: auto;
    height: ${theme.spacing.four};
    display: block;
    flex: none;
  `}
`

export const NewPillWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: 0;
  bottom: 0;
  right: 10px;
`

export const RelativeDiv = styled.div`
  position: relative;
  width: 100%;
`
